/**
 * DPGPersonality — Proactive nudge & personality engine for the Digital Personal Guard.
 *
 * Like Clippy, the DPG notices what you're doing and pops up with helpful tips,
 * warnings, or encouraging comments. But instead of "It looks like you're writing
 * a letter!", it says things like "I noticed you're checking an email — want me
 * to scan it for phishing red flags?"
 */

// ─── Contextual nudges based on current page ───────────────────────────
const PAGE_NUDGES = {
  Dashboard: [
    { key: "dpg.nudge.dashboard.0", mood: "happy", delay: 8000 },
    { key: "dpg.nudge.dashboard.1", mood: "greeting", delay: 5000 },
    { key: "dpg.nudge.dashboard.2", mood: "idle", delay: 15000 },
  ],
  EmailScamDetector: [
    { key: "dpg.nudge.email.0", mood: "scanning", delay: 3000 },
    { key: "dpg.nudge.email.1", mood: "concerned", delay: 10000 },
  ],
  TextScamDetector: [
    { key: "dpg.nudge.text.0", mood: "scanning", delay: 3000 },
  ],
  SocialMediaScamDetector: [
    { key: "dpg.nudge.social.0", mood: "alert", delay: 4000 },
  ],
  URLSafetyAnalysis: [
    { key: "dpg.nudge.url.0", mood: "scanning", delay: 3000 },
  ],
  SecurityTraining: [
    { key: "dpg.nudge.training.0", mood: "happy", delay: 5000 },
  ],
  ScamAlerts: [{ key: "dpg.nudge.alerts.0", mood: "alert", delay: 4000 }],
  Settings: [
    { key: "dpg.nudge.settings.0", mood: "concerned", delay: 6000 },
    { key: "dpg.nudge.settings.1", mood: "idle", delay: 10000 },
  ],
  MyPlan: [{ key: "dpg.nudge.plan.0", mood: "idle", delay: 5000 }],
};

// ─── Idle personality lines (shown when user hasn't interacted) ─────────
const IDLE_NUDGES = [
  { key: "dpg.nudge.idle.0", mood: "idle" },
  { key: "dpg.nudge.idle.1", mood: "concerned" },
  { key: "dpg.nudge.idle.2", mood: "greeting" },
  { key: "dpg.nudge.idle.3", mood: "alert" },
  { key: "dpg.nudge.idle.4", mood: "happy" },
  { key: "dpg.nudge.idle.5", mood: "happy" },
  { key: "dpg.nudge.idle.6", mood: "idle" },
];

// ─── Greeting variations by time of day ─────────────────────────────────
const TIME_GREETINGS = {
  morning: [
    "dpg.greeting.morning.0",
    "dpg.greeting.morning.1",
    "dpg.greeting.morning.2",
  ],
  afternoon: [
    "dpg.greeting.afternoon.0",
    "dpg.greeting.afternoon.1",
    "dpg.greeting.afternoon.2",
  ],
  evening: [
    "dpg.greeting.evening.0",
    "dpg.greeting.evening.1",
    "dpg.greeting.evening.2",
  ],
};

// ─── Reaction lines for specific events ─────────────────────────────────
const REACTIONS = {
  scamDetected: [
    "dpg.reaction.scam.0",
    "dpg.reaction.scam.1",
    "dpg.reaction.scam.2",
  ],
  safeResult: [
    "dpg.reaction.safe.0",
    "dpg.reaction.safe.1",
    "dpg.reaction.safe.2",
  ],
  userReturned: [
    "dpg.reaction.returned.0",
    "dpg.reaction.returned.1",
    "dpg.reaction.returned.2",
  ],
  firstVisit: ["dpg.reaction.first.0"],
  longInactive: ["dpg.reaction.inactive.0", "dpg.reaction.inactive.1"],
};

// ─── Personality engine ─────────────────────────────────────────────────

class DPGPersonality {
  constructor() {
    this.lastNudgeTime = 0;
    this.nudgeHistory = [];
    this.sessionStart = Date.now();
    this.currentPage = null;
    this.hasGreeted = false;
    this.nudgeCooldownMs = 120000; // 2 minutes between proactive nudges
    this.idleThresholdMs = 300000; // 5 minutes of no interaction = idle nudge
    this.lastInteraction = Date.now();
  }

  /**
   * Get a time-appropriate greeting for the user.
   */
  getGreeting(userName, t) {
    const hour = new Date().getHours();
    let period;
    if (hour < 12) period = "morning";
    else if (hour < 17) period = "afternoon";
    else period = "evening";

    const keys = TIME_GREETINGS[period];
    const key = this._pickRandom(keys);
    let text = t ? t(key) : key;

    if (userName) {
      text = text.replace("{name}", userName);
    } else {
      text = text.replace(", {name}", "").replace(",{name}", "");
    }
    return text;
  }

  /**
   * Get a contextual nudge based on the current page.
   * Returns { message, mood, delay } or null if no nudge is appropriate.
   */
  getPageNudge(page, t) {
    if (!this._canNudge()) return null;

    const nudges = PAGE_NUDGES[page];
    if (!nudges || nudges.length === 0) return null;

    // Avoid repeating the same nudge
    const unused = nudges.filter((n) => !this.nudgeHistory.includes(n.key));
    const pool = unused.length > 0 ? unused : nudges;
    const nudge = this._pickRandom(pool);

    this._recordNudge(nudge.key);
    return {
      message: t ? t(nudge.key) : nudge.key,
      mood: nudge.mood,
      delay: nudge.delay,
    };
  }

  /**
   * Get an idle nudge when the user hasn't interacted for a while.
   */
  getIdleNudge(t) {
    if (!this._canNudge()) return null;
    if (Date.now() - this.lastInteraction < this.idleThresholdMs) return null;

    const unused = IDLE_NUDGES.filter(
      (n) => !this.nudgeHistory.includes(n.key),
    );
    const pool = unused.length > 0 ? unused : IDLE_NUDGES;
    const nudge = this._pickRandom(pool);

    this._recordNudge(nudge.key);
    return { message: t ? t(nudge.key) : nudge.key, mood: nudge.mood };
  }

  /**
   * Get a reaction line for a specific event.
   */
  getReaction(event, t) {
    const keys = REACTIONS[event];
    if (!keys) return null;
    const key = this._pickRandom(keys);
    return t ? t(key) : key;
  }

  /**
   * Determine the mascot's mood based on app state.
   */
  getMood({
    isLoading,
    hasUnread,
    lastResultType,
    isOpen,
    timeSinceLastInteraction,
  }) {
    if (isLoading) return "thinking";
    if (lastResultType === "scam") return "alert";
    if (lastResultType === "safe") return "happy";
    if (hasUnread) return "alert";
    if (!isOpen && timeSinceLastInteraction > this.idleThresholdMs)
      return "sleeping";
    if (isOpen) return "greeting";
    return "idle";
  }

  /**
   * Record user interaction to reset idle timer.
   */
  recordInteraction() {
    this.lastInteraction = Date.now();
  }

  /**
   * Update the current page for context-aware nudges.
   */
  setPage(page) {
    this.currentPage = page;
    this.recordInteraction();
  }

  // ── Internal helpers ──────────────────────────────────────────────────

  _canNudge() {
    return Date.now() - this.lastNudgeTime > this.nudgeCooldownMs;
  }

  _recordNudge(message) {
    this.lastNudgeTime = Date.now();
    this.nudgeHistory.push(message);
    // Keep history manageable
    if (this.nudgeHistory.length > 20) {
      this.nudgeHistory = this.nudgeHistory.slice(-10);
    }
  }

  _pickRandom(arr) {
    return arr[Math.floor(Math.random() * arr.length)];
  }
}

// Export a singleton so personality state persists across renders
const dpgPersonality = new DPGPersonality();
export default dpgPersonality;
export { PAGE_NUDGES, IDLE_NUDGES, TIME_GREETINGS, REACTIONS };
